import { useEffect, useState } from 'react'
import type { Connection } from '@stride/shared'
import { useConnectionService } from '../../services/connections/ConnectionServiceContext'
import { useGitService } from '../../services/git'
import { useAppContext } from '../../context/AppContext'
import { setActiveGitConnectionId } from '../../lib/apiClient'
import { parseRepoUrl } from '../../lib/repoUrl'

// ── ActiveRepoSync ────────────────────────────────────────────────────────────
//
// Renderless component mounted inside AppShell (which sits under every provider).
// It bridges the connections store and AppContext:
//
//   1. Loads git connections (plain effect + state — no react-query, so chrome
//      doesn't require a QueryClientProvider in its tests).
//   2. Ensures activeConnectionId points at a real git connection; defaults to
//      the first one, or clears everything when there are no git connections.
//   3. Resolves the active connection's repo (parse repoUrl → getRepo) and
//      pushes it into selectedRepo / selectedBranch.
//
// AppContext stays pure state; all data-fetching lives here.

export function ActiveRepoSync(): null {
  const connSvc = useConnectionService()
  const git     = useGitService()
  const {
    activeConnectionId,
    setActiveConnectionId,
    setSelectedRepo,
    setSelectedBranch,
    selectedBranch,
    connectionsVersion,
  } = useAppContext()

  const [gitConnections, setGitConnections] = useState<Connection[]>([])

  // ── 1. Load git connections ───────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false
    connSvc.listConnections()
      .then((conns) => {
        if (cancelled) return
        setGitConnections(conns.filter((c) => c.kind === 'git'))
      })
      .catch(() => {
        if (cancelled) return
        setGitConnections([])
      })
    return () => { cancelled = true }
  // Same reason as the Sidebar: this lives in the shell, which never unmounts, so `[connSvc]` alone
  // read the connection list once at startup and never again.
  }, [connSvc, connectionsVersion])

  // ── 2. Reconcile activeConnectionId against available git connections ──────
  useEffect(() => {
    if (gitConnections.length === 0) {
      // No git connection at all → nothing to scope to.
      if (activeConnectionId !== null) setActiveConnectionId(null)
      setSelectedRepo(null)
      return
    }

    const stillValid = gitConnections.some((c) => c.id === activeConnectionId)
    if (!stillValid) {
      // Default to the first git connection.
      setActiveConnectionId(gitConnections[0]!.id)
    } else {
      // Keep apiClient threading in sync on mount (AppContext only syncs on set).
      setActiveGitConnectionId(activeConnectionId)
    }
  }, [gitConnections, activeConnectionId, setActiveConnectionId, setSelectedRepo])

  // ── 3. Resolve the active connection's repo ────────────────────────────────
  useEffect(() => {
    const active = gitConnections.find((c) => c.id === activeConnectionId)
    if (!active) return

    const ref = parseRepoUrl(active.repoUrl)
    if (!ref) {
      // Unparseable repoUrl — don't crash, just leave nothing selected.
      setSelectedRepo(null)
      return
    }

    let cancelled = false
    git.getRepo(ref)
      .then((repo) => {
        if (cancelled) return
        setSelectedRepo(repo)
        // Default the branch to the repo default only if the user hasn't picked
        // a non-default one yet ('main' is the untouched initial value).
        if (selectedBranch === 'main') setSelectedBranch(repo.defaultBranch)
      })
      .catch(() => {
        if (cancelled) return
        setSelectedRepo(null)
      })
    return () => { cancelled = true }
  }, [gitConnections, activeConnectionId, git, selectedBranch, setSelectedRepo, setSelectedBranch])

  return null
}
